import {
	RavenButton,
	RavenCopyText,
	RavenInputField,
	RavenTextTrim,
	toast,
} from '@ravenpay/raven-bank-ui';
import React, { useState } from 'react';
import '../UploadFile.css';
import { useDispatch, useSelector } from 'react-redux';
import {
	verifyCACDocuments,
	VerifyCACPayload,
} from '../../../../../../../redux/cac';
import RootState from '../../../../../../../redux/types';
import { useNavigate } from 'react-router-dom';
import { ALL_ROUTES } from '../../../../../../../routes/dashboard/overview/AllPathNames';

interface BusinessProps {
	rcNumber: string;
	id: string | number;
	businessEmail: string;
}

const CacDoc = ({ rcNumber, id, businessEmail }: BusinessProps) => {
	const [fileUploads, setFileUploads] = useState([
		{
			file: null as File | null,
			uploaded: false,
			fileName: '',
			title: 'CAC Certificate',
		},
		{
			file: null as File | null,
			uploaded: false,
			fileName: '',
			title: 'CAC Status Report',
		},
		{
			file: null as File | null,
			uploaded: false,
			fileName: '',
			title: 'MEMART Document',
		},
	]);

	const [uploaded, setUploaded] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { loading } = useSelector((state: RootState) => state.cac);

	const InitAiIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="19"
			height="12"
			fill="none"
			viewBox="0 0 19 12"
		>
			<path
				fill="#333"
				fillRule="evenodd"
				d="M.333 8.984c0 1.623 1.316 2.73 2.939 2.73H14.7c2.216-.317 3.919-2.06 3.919-4.362a4.57 4.57 0 0 0-5.243-4.523 5.224 5.224 0 0 0-9.738 3.216h-.04v.018a2.939 2.939 0 0 0-3.265 2.92m9.08 1.032a.833.833 0 0 1-.833-.833v-3.04l-.832.833a.833.833 0 0 1-1.18-1.178l2.256-2.257a.833.833 0 0 1 1.17-.008l2.317 2.257a.833.833 0 0 1-1.163 1.194l-.902-.878v3.077c0 .46-.373.833-.833.833"
				clipRule="evenodd"
			></path>
		</svg>
	);

	const uploadicon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="19"
			height="12"
			fill="none"
			viewBox="0 0 19 12"
		>
			<path
				fill="#1ACE37"
				fillRule="evenodd"
				d="M.333 8.984c0 1.623 1.316 2.73 2.939 2.73H14.7c2.216-.317 3.919-2.06 3.919-4.362a4.57 4.57 0 0 0-5.243-4.523 5.224 5.224 0 0 0-9.738 3.216h-.04v.018a2.939 2.939 0 0 0-3.265 2.92m9.08 1.032a.833.833 0 0 1-.833-.833v-3.04l-.832.833a.833.833 0 0 1-1.18-1.178l2.256-2.257a.833.833 0 0 1 1.17-.008l2.317 2.257a.833.833 0 0 1-1.163 1.194l-.902-.878v3.077c0 .46-.373.833-.833.833"
				clipRule="evenodd"
			></path>
		</svg>
	);

	const uploadcheck = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="16"
			height="16"
			fill="none"
			viewBox="0 0 16 16"
		>
			<path
				fill="#1ACE37"
				fillRule="evenodd"
				d="M8 15.2A7.2 7.2 0 1 0 8 .8a7.2 7.2 0 0 0 0 14.4m3.336-8.363a.9.9 0 1 0-1.273-1.273L7.1 8.528 5.936 7.364a.9.9 0 1 0-1.273 1.273l1.8 1.8a.9.9 0 0 0 1.273 0z"
				clipRule="evenodd"
			></path>
		</svg>
	);

	const handleFileChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const file = e.target.files?.[0];
		const fileTypes = ['image/jpeg', 'image/png', 'application/pdf'];

		if (!file) return;

		if (!fileTypes.includes(file.type)) {
			toast.error('Only JPG, PNG or PDF files are allowed.');
			return;
		}

		if (file.size > 2 * 1024 * 1024) {
			toast.error('File size exceeds 2MB');
			return;
		}

		const updated = [...fileUploads];
		updated[index] = {
			...updated[index],
			file,
			uploaded: true,
			fileName: file.name,
		};
		setFileUploads(updated);
	};

	const handleSubmit = async () => {
		if (fileUploads.some((file) => !file.uploaded || !file.file)) {
			toast.error('Please upload all required documents.');
			return;
		}

		try {
			const formData = new FormData();
			formData.append('rc_number', rcNumber);
			formData.append('business_id', id.toString());
			formData.append('email', businessEmail);
			formData.append('cac_document', fileUploads[0].file!);
			formData.append('cac_status_report', fileUploads[1].file!);
			formData.append('memart', fileUploads[2].file!);

			const response = await dispatch(verifyCACDocuments(formData));

			if (response?.payload?.status === 'success') {
				// toast.success('Documents submitted successfully');
				navigate(ALL_ROUTES?.COMPLIANCE_CAC_VERIFICATION);
			} else {
				toast.error(response?.payload?.message || 'Upload failed');
			}
		} catch (error: any) {
			console.error('Document upload error:', error);
			// const errorMessage = error?.message || 'Something went wrong';
			// toast.error(errorMessage);
		}
	};

	return (
		<>
			<main className="main slide-in-right">
				<p
					style={{ color: 'rgba(139, 139, 139, 1)', marginBottom: '1.5rem' }}
					className="section-title"
				>
					CAC DOCUMENTS
				</p>
				<h1 className="form-title">
					Upload <span className="hight-name">BeaKiosk Mart</span> <br /> CAC
					Documents
				</h1>

				{/* Upload Card */}
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(2, 1fr)',
						gap: '2rem',
						maxWidth: '100%',
					}}
				>
					{fileUploads.map((upload, index) => (
						<div
							key={index}
							className={`upload-card ${upload.uploaded ? 'uploaded' : ''}`}
						>
							<div className="card-header">
								<span>{upload.title}</span>
								{upload.uploaded && (
									<span className="check-icon">
										<figure>{uploadcheck}</figure>
									</span>
								)}
							</div>

							<div style={{ backgroundColor: 'white', borderRadius: '1rem' }}>
								<label
									className="upload-box"
									htmlFor={`file-input-${index}`}
									style={{ cursor: 'pointer' }}
								>
									<div
										className="upload-icon"
										style={{
											backgroundColor: upload.uploaded
												? 'rgba(232, 255, 246, 1)'
												: '',
										}}
									>
										<figure
											style={{
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												width: '5rem',
												height: '5rem',
											}}
										>
											{upload.uploaded ? uploadicon : InitAiIcon}
										</figure>
									</div>

									<p
										style={{
											textAlign: 'center',
											marginTop: '1rem',
											fontStyle: upload.uploaded ? 'italic' : 'normal',
										}}
									>
										{RavenTextTrim(
											upload.uploaded ? upload.fileName : 'Click to Upload',
											20
										)}
									</p>

									<p
										style={{
											textAlign: 'center',
											color: 'rgba(128, 128, 128, 1)',
											fontSize: '1rem',
											fontWeight: 400,
											marginTop: '1rem',
										}}
									>
										JPG, PNG or GIF <br /> Max file size 2MB
									</p>
								</label>
							</div>

							<RavenInputField
								id={`file-input-${index}`}
								type="file"
								style={{ display: 'none' }}
								// accept=".jpg,.jpeg,.png,.gif"
								onChange={(e: any) => handleFileChange(e, index)}
							/>
						</div>
					))}
				</div>

				{/* Continue Button */}
				<RavenButton
					disabled={!fileUploads.every((file) => file.uploaded && file.file)}
					loading={loading}
					style={{ marginTop: '4rem' }}
					label="Continue"
					color="deep-green-dark"
					onClick={() => {
						handleSubmit();
					}}
				/>
			</main>
		</>
	);
};

export default CacDoc;
